<template>
  <div class="root">
    <label v-if="label" class="field-label">{{ label }}</label>
    <div class="input-box" :class="{ disabled: processing || disabled }">
      <FileSelect
        class="file-btn"
        accept=".docx,.pptx,.pptm,.pdf,.txt,.eml,.md"
        title="From file"
        :disabled="processing || disabled"
        :multiple="true"
        @change="filesSelected"
      >
        <MdIcon size="md" :name="processing ? 'progress-clock' : 'paperclip'" />
      </FileSelect>

      <div class="content">
        <!-- Source file chips (auto-populated from doc code) -->
        <div v-if="localSourceFiles && localSourceFiles.length" class="chips">
          <span v-for="(sf, i) in localSourceFiles" :key="i" class="chip" :title="sf.content">
            <MdIcon name="eye-outline" size="sm" class="chip-eye" />
            <span class="chip-name">{{ sf.name }}</span>
            <button v-if="!disabled" class="chip-remove" type="button" @click="removeSourceFile(i)">
              <MdIcon name="close" size="sm" />
            </button>
          </span>
        </div>

        <textarea v-model="localValue" class="text-input" placeholder="Enter text" :disabled="processing || disabled" rows="1" @input="onChange" />
      </div>
    </div>
  </div>
</template>
<script>
import { mapState } from 'vuex';
import MdIcon from '@/components/common/MdIcon';
import FileSelect from '@/components/common/FileSelect';
import { convertFiles } from '@/utils/filesConverter';

export default {
  components: {
    MdIcon,
    FileSelect
  },
  props: {
    value: {
      type: String,
      default: '',
      required: false
    },
    sourceFiles: {
      type: Array,
      default: () => []
    },
    context: {
      type: Object,
      default: null,
      required: false
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    label: {
      type: String,
      required: false,
      default: ''
    },
    multiline: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      localValue: this.value,
      localSourceFiles: [...(this.sourceFiles || [])],
      processing: false
    };
  },
  computed: {
    ...mapState({
      organizations: s => s.organizations.collection
    })
  },
  watch: {
    sourceFiles(val) {
      this.localSourceFiles = [...(val || [])];
    },
    value(val) {
      if (val !== this.localValue) this.localValue = val;
    }
  },
  methods: {
    onChange() {
      this.$emit('input', this.localValue);
    },
    removeSourceFile(index) {
      this.localSourceFiles.splice(index, 1);
      this.$emit('update:sourceFiles', [...this.localSourceFiles]);
    },
    getClientOrganization() {
      if (!this.context?.invention) return null;
      const reference = this.context.invention.references?.find(r => r.startsWith('HARRITY:'));
      if (!reference) return null;
      const alias = reference
        .split(':')
        .pop()
        .split('-')[0];
      return this.organizations?.find(o => o.aliases.includes(alias))?.id ?? null;
    },
    async filesSelected(files) {
      if (!files.length) return;
      try {
        this.processing = true;
        const result = await convertFiles(files, { organizationId: this.getClientOrganization() }, { timeoutMs: 1000 * 60 * 5 });
        if (!result.length) return;

        const newChips = [];
        let textData = '';

        for (const file of result) {
          if (file.error) {
            textData += `Failed to convert ${file.name}: ${file.error}\n`;
            continue;
          }
          if (file.type === 'text') {
            newChips.push({ name: file.name, content: file.content, type: 'text' });
          } else if (file.type === 'pages') {
            const text = file.content.map(p => p.content).join('\n');
            newChips.push({ name: file.name, content: text, type: 'text' });
          }
        }

        if (newChips.length) {
          this.localSourceFiles = [...this.localSourceFiles, ...newChips];
          this.$emit('update:sourceFiles', [...this.localSourceFiles]);
          this.localValue = '';
          this.$emit('input', this.localValue);
        } else if (textData) {
          this.localValue = (this.localValue ? this.localValue + '\n' : '') + textData;
          this.$emit('input', this.localValue);
        }
      } finally {
        this.processing = false;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.root {
  .field-label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    opacity: 0.65;
    margin-bottom: 4px;
  }

  .input-box {
    display: flex;
    align-items: center;
    gap: 2px;
    border: 1px solid white;
    border-radius: 6px;
    padding: 1px 4px;
    background: var(--theme-surface);
    transition: border-color 0.15s;

    &:focus-within {
      border-color: var(--theme-primary);
      box-shadow: 0 0 0 2px rgba(var(--theme-primary-rgb, 90, 90, 200), 0.15);
    }

    &.disabled {
      opacity: 0.6;
    }

    .file-btn {
      padding: 0;
      margin-top: 2px;
      flex-shrink: 0;
      color: white;
      opacity: 0.7;

      &:hover {
        opacity: 1;
      }
    }

    .content {
      flex: 1;
      min-width: 0;

      .chips {
        display: flex;
        flex-wrap: wrap;
        gap: 4px;
        margin-top: 4px;
        margin-bottom: 4px;

        .chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px 2px 5px;
          background: rgba(var(--theme-primary-rgb, 90, 90, 200), 0.1);
          border: 1px solid rgba(var(--theme-primary-rgb, 90, 90, 200), 0.35);
          border-radius: 20px;
          font-size: 12px;
          color: var(--theme-on-surface);
          max-width: 180px;
          cursor: default;

          .chip-eye {
            color: var(--theme-primary);
            flex-shrink: 0;
            font-size: 14px;
          }

          .chip-name {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .chip-remove {
            background: none;
            border: none;
            padding: 0;
            cursor: pointer;
            display: flex;
            align-items: center;
            color: var(--theme-on-surface);
            opacity: 0.6;
            flex-shrink: 0;

            &:hover {
              opacity: 1;
            }
          }
        }
      }

      .text-input {
        width: 100%;
        border: none;
        outline: none;
        background: transparent;
        resize: none;
        font-size: 13px;
        color: var(--theme-on-surface);
        font-family: inherit;
        min-height: unset;
        height: 24px;
        line-height: 24px;
        vertical-align: middle;

        &::placeholder {
          color: var(--theme-on-surface);
          opacity: 0.4;
        }

        &:disabled {
          cursor: default;
        }
      }
    }
  }
}
</style>
